import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { uploadData, remove, getUrl } from 'aws-amplify/storage';
import {
  createNetworkError,
  createValidationError,
  createDataUnavailableError,
  isErrorResponse,
  logError
} from '../utils/StandardErrorFormat.js';

class BrandingService {
  constructor() {
    this.logger = logger.child('BrandingService');
    this.client = generateClient({ authMode: 'userPool' });
  }

  async getUserBranding(userId) {
    try {
      // Check if client and models are available
      if (!this.client || !this.client.models || !this.client.models.UserBranding) {
        this.logger.warn('GraphQL client or UserBranding model not available, returning null');
        return null;
      }

      this.logger.info('Fetching branding for userId:', userId);
      const { data: brandings, errors } = await this.client.models.UserBranding.list({
        filter: { userId: { eq: userId } },
      });

      if (errors) {
        this.logger.error('GraphQL errors:', errors);
        return createNetworkError(errors[0]?.message || 'GraphQL query failed');
      }

      this.logger.info('Fetched brandings:', brandings);

      if (brandings && brandings.length > 0) {
        const branding = brandings[0];

        // Generate fresh URLs from S3 keys
        const [logoPrimaryUrl, logoWhiteUrl, logoDarkUrl, signatureImageUrl] = await Promise.all([
          branding.logoPrimaryS3Key ? this.getSignedUrl(branding.logoPrimaryS3Key) : null,
          branding.logoWhiteS3Key ? this.getSignedUrl(branding.logoWhiteS3Key) : null,
          branding.logoDarkS3Key ? this.getSignedUrl(branding.logoDarkS3Key) : null,
          branding.signatureImageS3Key ? this.getSignedUrl(branding.signatureImageS3Key) : null,
        ]);

        // Parse JSON fields back to objects/arrays for the frontend
        return {
          ...branding,
          // Override stored URLs with fresh signed URLs
          logoPrimaryUrl: logoPrimaryUrl || branding.logoPrimaryUrl,
          logoWhiteUrl: logoWhiteUrl || branding.logoWhiteUrl,
          logoDarkUrl: logoDarkUrl || branding.logoDarkUrl,
          signatureImageUrl: signatureImageUrl || branding.signatureImageUrl,
          disclaimers: branding.disclaimers
            ? this.safeJsonParse(branding.disclaimers, [])
            : [],
          logoMetadata: branding.logoMetadata
            ? this.safeJsonParse(branding.logoMetadata, null)
            : null,
          versionHistory: branding.versionHistory
            ? this.safeJsonParse(branding.versionHistory, [])
            : [],
        };
      }

      return null;
    } catch (error) {
      this.logger.error('Error fetching user branding:', error);
      return createNetworkError(error.message || 'Failed to fetch branding settings', error);
    }
  }

  // Helper method to generate signed URLs from S3 keys
  async getSignedUrl(s3Key) {
    if (!s3Key) return null;
    try {
      // For Amplify v6 with named buckets, we need to specify the bucket
      // The bucket name in the config is 'profilePictures'
      const urlResult = await getUrl({
        path: s3Key,
        options: {
          bucket: 'profilePictures', // Specify the bucket name from config
          expiresIn: 3600 // 1 hour instead of 15 minutes
        }
      });
      return urlResult.url.toString();
    } catch (error) {
      this.logger.error(`Error generating signed URL for ${s3Key}:`, error);
      return null;
    }
  }

  // Helper method for safe JSON parsing
  safeJsonParse(jsonString, defaultValue = null) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      this.logger.warn('Failed to parse JSON:', error);
      return defaultValue;
    }
  }

  async createBranding(brandingData) {
    try {
      // Check if client and models are available
      if (!this.client || !this.client.models || !this.client.models.UserBranding) {
        this.logger.warn('GraphQL client or UserBranding model not available, cannot create branding');
        return null;
      }

      // Prepare data with JSON fields properly stringified
      const newBranding = {
        ...brandingData,
        // Stringify JSON fields (schema expects JSON strings, not objects)
        disclaimers: brandingData.disclaimers
          ? JSON.stringify(brandingData.disclaimers)
          : JSON.stringify([]),
        logoMetadata: brandingData.logoMetadata
          ? JSON.stringify(brandingData.logoMetadata)
          : null,
        version: 1,
        versionHistory: JSON.stringify([]),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      this.logger.info('Creating branding with data:', newBranding);

      const { data, errors } = await this.client.models.UserBranding.create(newBranding);

      if (errors) {
        this.logger.error('GraphQL errors:', errors);
        return createNetworkError(errors[0]?.message || 'Failed to create branding');
      }

      return data;
    } catch (error) {
      this.logger.error('Error creating branding:', error);
      return createNetworkError(error.message || 'Failed to create branding settings', error);
    }
  }

  async updateBranding(id, updates) {
    try {
      // Check if client and models are available
      if (!this.client || !this.client.models || !this.client.models.UserBranding) {
        this.logger.warn('GraphQL client or UserBranding model not available, cannot update branding');
        return null;
      }

      const existing = await this.getBrandingById(id);

      // Check if getBrandingById returned an error
      if (isErrorResponse(existing)) {
        return existing;
      }

      if (!existing) {
        return createDataUnavailableError('Branding settings not found');
      }

      const currentVersion = existing.version || 1;
      let versionHistory = [];

      try {
        versionHistory = existing.versionHistory ? JSON.parse(existing.versionHistory) : [];
      } catch (e) {
        versionHistory = [];
      }

      const historyEntry = {
        version: currentVersion,
        timestamp: new Date().toISOString(),
        changes: updates,
      };

      versionHistory.unshift(historyEntry);
      if (versionHistory.length > 10) {
        versionHistory = versionHistory.slice(0, 10);
      }

      // Prepare updates with JSON fields properly stringified
      const updatedData = {
        ...updates,
        // Stringify JSON fields if they exist in updates
        disclaimers: updates.disclaimers
          ? JSON.stringify(updates.disclaimers)
          : undefined,
        logoMetadata: updates.logoMetadata
          ? JSON.stringify(updates.logoMetadata)
          : undefined,
        version: currentVersion + 1,
        versionHistory: JSON.stringify(versionHistory),
        updatedAt: new Date().toISOString(),
      };

      // Remove undefined values to avoid sending null updates
      Object.keys(updatedData).forEach(
        key => updatedData[key] === undefined && delete updatedData[key]
      );

      this.logger.info('Updating branding with data:', updatedData);

      const { data, errors } = await this.client.models.UserBranding.update({
        id,
        ...updatedData,
      });

      if (errors) {
        this.logger.error('GraphQL errors:', errors);
        return createNetworkError(errors[0]?.message || 'Failed to update branding');
      }

      return data;
    } catch (error) {
      this.logger.error('Error updating branding:', error);
      return createNetworkError(error.message || 'Failed to update branding settings', error);
    }
  }

  async deleteBranding(id) {
    try {
      // Check if client and models are available
      if (!this.client || !this.client.models || !this.client.models.UserBranding) {
        this.logger.warn('GraphQL client or UserBranding model not available, cannot delete branding');
        return false;
      }

      const branding = await this.getBrandingById(id);

      // Check if getBrandingById returned an error
      if (isErrorResponse(branding)) {
        return branding;
      }

      if (!branding) {
        return createDataUnavailableError('Branding settings not found');
      }

      const s3KeysToDelete = [
        branding.logoPrimaryS3Key,
        branding.logoWhiteS3Key,
        branding.logoDarkS3Key,
        branding.signatureImageS3Key,
      ].filter(Boolean);

      await Promise.all(
        s3KeysToDelete.map((key) => this.deleteFile(key))
      );

      const { data, errors } = await this.client.models.UserBranding.delete({ id });

      if (errors) {
        this.logger.error('GraphQL errors:', errors);
        return createNetworkError(errors[0]?.message || 'Failed to delete branding');
      }

      return data;
    } catch (error) {
      this.logger.error('Error deleting branding:', error);
      return createNetworkError(error.message || 'Failed to delete branding settings', error);
    }
  }

  async getBrandingById(id) {
    try {
      // Check if client and models are available
      if (!this.client || !this.client.models || !this.client.models.UserBranding) {
        this.logger.warn('GraphQL client or UserBranding model not available, returning null');
        return null;
      }
      const { data } = await this.client.models.UserBranding.get({ id });

      if (!data) return null;

      // Generate fresh URLs from S3 keys
      const [logoPrimaryUrl, logoWhiteUrl, logoDarkUrl, signatureImageUrl] = await Promise.all([
        data.logoPrimaryS3Key ? this.getSignedUrl(data.logoPrimaryS3Key) : null,
        data.logoWhiteS3Key ? this.getSignedUrl(data.logoWhiteS3Key) : null,
        data.logoDarkS3Key ? this.getSignedUrl(data.logoDarkS3Key) : null,
        data.signatureImageS3Key ? this.getSignedUrl(data.signatureImageS3Key) : null,
      ]);

      // Parse JSON fields back to objects/arrays for the frontend
      return {
        ...data,
        // Override stored URLs with fresh signed URLs
        logoPrimaryUrl: logoPrimaryUrl || data.logoPrimaryUrl,
        logoWhiteUrl: logoWhiteUrl || data.logoWhiteUrl,
        logoDarkUrl: logoDarkUrl || data.logoDarkUrl,
        signatureImageUrl: signatureImageUrl || data.signatureImageUrl,
        disclaimers: data.disclaimers
          ? this.safeJsonParse(data.disclaimers, [])
          : [],
        logoMetadata: data.logoMetadata
          ? this.safeJsonParse(data.logoMetadata, null)
          : null,
        versionHistory: data.versionHistory
          ? this.safeJsonParse(data.versionHistory, [])
          : [],
      };
    } catch (error) {
      this.logger.error('Error fetching branding by ID:', error);
      return null;
    }
  }

  async uploadLogo(file, logoType, userId) {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${logoType}-${Date.now()}.${fileExtension}`;
      const s3Key = `branding/logos/${userId}/${fileName}`;

      const result = await uploadData({
        path: s3Key,
        data: file,
        options: {
          contentType: file.type,
          bucket: 'profilePictures' // Specify the bucket name
        },
      }).result;

      const urlResult = await getUrl({
        path: s3Key,
        options: {
          bucket: 'profilePictures' // Specify the bucket name
        }
      });

      return {
        s3Key: result.path,
        url: urlResult.url.toString(),
        metadata: {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      this.logger.error('Error uploading logo:', error);
      return createNetworkError('Failed to upload logo', error);
    }
  }

  async uploadSignature(file, userId) {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `signature-${Date.now()}.${fileExtension}`;
      const s3Key = `branding/signatures/${userId}/${fileName}`;

      const result = await uploadData({
        path: s3Key,
        data: file,
        options: {
          contentType: file.type,
          bucket: 'profilePictures' // Specify the bucket name
        },
      }).result;

      const urlResult = await getUrl({
        path: s3Key,
        options: {
          bucket: 'profilePictures' // Specify the bucket name
        }
      });

      return {
        s3Key: result.path,
        url: urlResult.url.toString(),
        metadata: {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      this.logger.error('Error uploading signature:', error);
      return createNetworkError('Failed to upload signature image', error);
    }
  }

  async deleteFile(s3Key) {
    try {
      if (!s3Key) return;
      await remove({
        path: s3Key,
        options: {
          bucket: 'profilePictures' // Specify the bucket name
        }
      });
    } catch (error) {
      this.logger.error('Error deleting file from S3:', error);
    }
  }

  htmlToPlainText(html) {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  getSignOffText(signOffTemplate, customText) {
    const templates = {
      professional: 'Best regards,',
      warm: 'Warm regards,',
      casual: 'Cheers,',
      custom: customText || 'Best regards,',
    };

    return templates[signOffTemplate] || templates.professional;
  }

  getDefaultDisclaimers() {
    return [
      {
        id: 'general',
        text: 'This newsletter is for informational purposes only and does not constitute financial advice.',
        enabled: true,
        position: 'footer',
      },
      {
        id: 'risk',
        text: 'Past performance is not indicative of future results. All investments carry risk.',
        enabled: true,
        position: 'footer',
      },
      {
        id: 'confidential',
        text: 'This communication is confidential and intended solely for the addressee.',
        enabled: false,
        position: 'footer',
      },
    ];
  }

  async getVersionHistory(id) {
    try {
      const branding = await this.getBrandingById(id);
      if (!branding || !branding.versionHistory) {
        return [];
      }

      return JSON.parse(branding.versionHistory);
    } catch (error) {
      this.logger.error('Error fetching version history:', error);
      return [];
    }
  }

  async restoreVersion(id, versionNumber) {
    try {
      const history = await this.getVersionHistory(id);

      // Check if getVersionHistory returned an error
      if (isErrorResponse(history)) {
        return history;
      }

      const targetVersion = history.find((v) => v.version === versionNumber);

      if (!targetVersion) {
        return createDataUnavailableError('Version not found');
      }

      const result = await this.updateBranding(id, targetVersion.changes);

      // Propagate error if updateBranding failed
      if (isErrorResponse(result)) {
        return result;
      }

      return result;
    } catch (error) {
      this.logger.error('Error restoring version:', error);
      return createNetworkError('Failed to restore version', error);
    }
  }
}

// Export both class and singleton instance
export { BrandingService };
export default new BrandingService();
